import React, { useMemo, useState } from 'react';
import { Drawer, Tooltip } from 'antd';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { AiChatView } from '@/components/common/AiChat/AiChatView';
import { useActiveAiGate } from '@/components/common/aiAssistant/useActiveAiGate';
import { useAiChat } from '@/components/common/AiChat/useAiChat';
import { buildActionPlanV2AiPageContext } from '@/components/common/ActionPlanV2/actionPlanAiPageContext';
import {
  useActionPlanAiChatConfirm,
  useActionPlanAiChatConfirmAll,
  useActionPlanAiObjectivePick,
  useActionPlanAiTaskListPick,
} from '@/components/common/ActionPlanV2/actionPlanAiChatHandlers';
import { usePrivateStore } from '@/store/usePrivateStore';
import { canUseActionPlanV2AiAssistant } from '@/components/common/ActionPlanV2/constants';
import { Apv2TipsButton } from '@/components/common/ActionPlanV2/ui/TipsButton';
import { useAuthStore } from '@/store/useAuthStore';
import { actionPlanAiSuggestions } from '@/components/common/ActionPlanV2/actionPlanAiSuggestions';
import panelStyles from '@/components/common/AiAssistantPanel.module.css';
import chatStyles from '@/components/common/AiChat/AiChatView.module.css';
import styles from './FloatingAiAssistant.module.css';

/** `/action-plan-v2` và mọi route con. */
export function isActionPlanV2AiPath(pathname: string): boolean {
  const path = (pathname || '').split('?')[0].split('#')[0].replace(/\/$/, '') || '/';
  return path === '/action-plan-v2' || path.startsWith('/action-plan-v2/');
}

const ASSISTANT_NAME = 'Trợ lý BSC';

/** Trợ lý Action Plan V2 — chat qua MS.AICoaching (không MS.QuyTrinh). */
export const FloatingAiAssistant: React.FC = () => {
  const router = useRouter();
  const email = useAuthStore((state) => state.email);
  const profile = useAuthStore((state) => state.profile);
  const profileId = profile?.id ?? '';
  const idSelect = usePrivateStore((s) => s.getRequestHeader().selectTree?.idSelected || '');
  const storeYear = usePrivateStore((s) => s.giaiDoan?.year);
  // Cùng cổng với bubble Hub: đăng nhập + config_setting_active_ai. AI tắt → ẩn bubble và không gọi API AI Hub.
  const { ready: aiReady } = useActiveAiGate();
  const visible = aiReady && isActionPlanV2AiPath(router.pathname || '/') && canUseActionPlanV2AiAssistant(email);
  const [open, setOpen] = useState(false);

  const pageContext = useMemo(
    () =>
      buildActionPlanV2AiPageContext(
        router.asPath || '/action-plan-v2',
        idSelect || profileId,
        storeYear ?? new Date().getFullYear(),
      ),
    [router.asPath, profileId, idSelect, storeYear],
  );
  const chat = useAiChat({ pageContext, enabled: visible });
  const handleConfirm = useActionPlanAiChatConfirm(chat);
  const handleConfirmAll = useActionPlanAiChatConfirmAll(chat, handleConfirm);
  const handleTaskPick = useActionPlanAiTaskListPick(chat);
  const handleObjectivePick = useActionPlanAiObjectivePick(chat);

  if (!visible) return null;

  return (
    <>
      {!open ? (
        <Tooltip title={ASSISTANT_NAME} placement="left">
          <span className={`${styles.anchor} fixed bottom-14 right-6 z-[210]`}>
            <span className={styles.ring} aria-hidden />
            <button
              type="button"
              aria-label={ASSISTANT_NAME}
              onClick={() => setOpen(true)}
              className={`${styles.button} flex h-12 w-12 items-center justify-center rounded-full border-0 bg-[#4285F4] text-white shadow-[0_4px_16px_rgba(66,133,244,0.45)] hover:brightness-110`}
            >
              <svg className={styles.icon} width="28" height="28" viewBox="0 0 24 24" fill="none" color="#fff" aria-hidden>
                <path
                  d="M6.2 5.4h11.6A2.2 2.2 0 0 1 20 7.6v7.1a2.2 2.2 0 0 1-2.2 2.2h-6.1L8 20.2v-3.3H6.2A2.2 2.2 0 0 1 4 14.7V7.6a2.2 2.2 0 0 1 2.2-2.2Z"
                  stroke="#fff"
                  strokeWidth="1.7"
                  strokeLinejoin="round"
                />
                <circle cx="9.4" cy="10.4" r="1" fill="#fff" />
                <circle cx="14.6" cy="10.4" r="1" fill="#fff" />
                <path d="M9.3 13c1.2 1 4.2 1 5.4 0" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
            </button>
          </span>
        </Tooltip>
      ) : null}
      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        width="50%"
        className={`${panelStyles.drawer} ${chatStyles.chatDrawer}`}
        title={(
          <div className={chatStyles.drawerHeader}>
            <div className={chatStyles.drawerBrand}>
              <span className={chatStyles.drawerAvatar}>
                <Image src="/icon/agent.png" alt="" width={28} height={28} unoptimized />
              </span>
              <span className={chatStyles.drawerTitle}>{ASSISTANT_NAME}</span>
              <span className={chatStyles.drawerContext}>Action Plan V2</span>
            </div>
            <div className={chatStyles.drawerActions}>
              <span className={chatStyles.headerTips}>
                <Apv2TipsButton variant="assistant" />
              </span>
            </div>
          </div>
        )}
        destroyOnHidden={false}
        styles={{ body: { padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}
      >
        <AiChatView
          turns={chat.turns}
          streaming={chat.streaming}
          lastPrompt={chat.lastPrompt}
          onSend={(m) => void chat.send(m)}
          onStop={chat.stop}
          onActionPlanPendingChange={chat.updateActionPlanPending}
          onActionPlanConfirm={handleConfirm}
          onActionPlanConfirmAll={handleConfirmAll}
          onActionPlanTaskListChange={chat.updateActionPlanTaskList}
          onActionPlanAssigneeChange={chat.updateActionPlanAssigneeList}
          onActionPlanTaskPick={handleTaskPick}
          onActionPlanObjectivePick={handleObjectivePick}
          llmConfig={chat.llmConfig}
          onLlmConfigChange={chat.setLlmConfig}
          llmCatalog={chat.llmCatalog}
          contextLabel="Action Plan V2"
          suggestions={actionPlanAiSuggestions(profile)}
          assistantName={ASSISTANT_NAME}
          contextInHeader
        />
      </Drawer>
    </>
  );
};
